import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/lib/utils';
import { contractsApi, contractsQueryKeys } from '../api';
import type {
  ContractFilters,
  CreateContractDto,
  UpdateContractDto,
  CreateBillingPeriodDto,
} from '../types';

// Hook for fetching contracts list
export function useContractsList(filters?: ContractFilters) {
  return useQuery({
    queryKey: contractsQueryKeys.list(filters),
    queryFn: () => contractsApi.getAll(filters),
    staleTime: 5 * 60 * 1000,
  });
}

// Hook for fetching single contract
export function useContract(id: number | string | undefined) {
  return useQuery({
    queryKey: contractsQueryKeys.detail(id ?? ''),
    queryFn: () => contractsApi.getById(id!),
    enabled: !!id,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook for contract mutations
export function useContractMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data: CreateContractDto) => contractsApi.create(data),
    onSuccess: () => {
      toast.success(t('contractCreatedSuccessfully'));
      void queryClient.invalidateQueries({ queryKey: contractsQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorCreatingContract')));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: UpdateContractDto }) =>
      contractsApi.update({ id, data }),
    onSuccess: (_, { id }) => {
      toast.success(t('contractUpdatedSuccessfully'));
      void queryClient.invalidateQueries({ queryKey: contractsQueryKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: contractsQueryKeys.detail(id) });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorUpdatingContract')));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number | string) => contractsApi.delete(id),
    onSuccess: () => {
      toast.success(t('contractDeletedSuccessfully'));
      void queryClient.invalidateQueries({ queryKey: contractsQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorDeletingContract')));
    },
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
  };
}

// Hook for contract status changes
export function useContractStatusMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const changeStatusMutation = useMutation({
    mutationFn: ({ url, id }: { url: string; id: number | string }) =>
      contractsApi.changeStatus({ url, id }),
    onSuccess: (_, { id }) => {
      toast.success(t('statusChangedSuccessfully'));
      void queryClient.invalidateQueries({ queryKey: contractsQueryKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: contractsQueryKeys.detail(id) });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('failedToChangeStatus')));
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: number | string) => contractsApi.activate(id),
    onSuccess: (_, id) => {
      toast.success(t('contractActivatedSuccessfully'));
      void queryClient.invalidateQueries({ queryKey: contractsQueryKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: contractsQueryKeys.detail(id) });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('failedToActivateContract')));
    },
  });

  const closeMutation = useMutation({
    mutationFn: (id: number | string) => contractsApi.close(id),
    onSuccess: (_, id) => {
      toast.success(t('contractClosedSuccessfully'));
      void queryClient.invalidateQueries({ queryKey: contractsQueryKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: contractsQueryKeys.detail(id) });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('failedToCloseContract')));
    },
  });

  const renewMutation = useMutation({
    mutationFn: (id: number | string) => contractsApi.renew(id),
    onSuccess: (_, id) => {
      toast.success(t('contractRenewedSuccessfully'));
      void queryClient.invalidateQueries({ queryKey: contractsQueryKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: contractsQueryKeys.detail(id) });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('failedToRenewContract')));
    },
  });

  // Re-prices every task of an HOURLY contract. The toast reports what moved, so
  // a no-op re-price is visibly a no-op rather than an unexplained success.
  const recalculateMutation = useMutation({
    mutationFn: (id: number | string) => contractsApi.recalculate(id),
    onSuccess: (summary, id) => {
      toast.success(
        summary?.tasksChanged
          ? t('contractRecalculatedWithChanges', {
              changed: summary.tasksChanged,
              total: summary.tasksReplayed,
            })
          : t('contractRecalculatedNoChanges', { total: summary?.tasksReplayed ?? 0 }),
      );
      void queryClient.invalidateQueries({ queryKey: contractsQueryKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: contractsQueryKeys.detail(id) });
      // Task prices and period durations moved — every report reading them is stale.
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
      void queryClient.invalidateQueries({ queryKey: ['reportBillingPeriods'] });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('failedToRecalculateContract')));
    },
  });

  return {
    changeStatusMutation,
    activateMutation,
    closeMutation,
    renewMutation,
    recalculateMutation,
  };
}

// Hook for billing period mutations
export function useBillingPeriodMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const startBillingPeriodMutation = useMutation({
    mutationFn: (data: CreateBillingPeriodDto) => contractsApi.startBillingPeriod(data),
    onSuccess: (_, { contractId }) => {
      toast.success(t('billingPeriodStartedSuccessfully'));
      void queryClient.invalidateQueries({ queryKey: contractsQueryKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: contractsQueryKeys.detail(contractId) });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('failedToStartBillingPeriod')));
    },
  });

  const stopBillingPeriodMutation = useMutation({
    mutationFn: (id: number | string) => contractsApi.stopBillingPeriod(id),
    onSuccess: () => {
      toast.success(t('billingPeriodStoppedSuccessfully'));
      void queryClient.invalidateQueries({ queryKey: contractsQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('failedToStopBillingPeriod')));
    },
  });

  return {
    startBillingPeriodMutation,
    stopBillingPeriodMutation,
  };
}

// Hook for currency rate
export function useCurrencyRate() {
  return useQuery({
    queryKey: ['currencyRate'],
    queryFn: () => contractsApi.getCurrencyRate(),
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}
